import { useCallback, useEffect, useRef, useState } from 'react';
import { usePowerSyncDb } from '../adapters/powersync';

// ─── Types ───

export interface MilestoneStatus {
  key: string;
  label: string;
  done: boolean;
  count: number;
  sanityOk: boolean;
}

export interface InitialSyncMilestones {
  milestones: MilestoneStatus[];
  allComplete: boolean;
  progress: number;
}

// ─── Milestone Definitions ───

interface MilestoneDef {
  key: string;
  label: string;
  countSql: string;
  /** Optional sanity-check query — must return { ok: 1 } to pass */
  sanitySql?: string;
}

const MILESTONE_DEFS: MilestoneDef[] = [
  {
    key: 'appConfig',
    label: 'Clinic configuration',
    countSql: 'SELECT COUNT(*) AS cnt FROM app_config',
  },
  {
    key: 'vitalDefinitions',
    label: 'Vital definitions',
    countSql: 'SELECT COUNT(*) AS cnt FROM vital_definitions WHERE deleted_at IS NULL',
  },
  {
    key: 'calendarSettings',
    label: 'Calendar settings',
    countSql: 'SELECT COUNT(*) AS cnt FROM appointment_calendar_settings',
    sanitySql: `SELECT CASE
      WHEN COUNT(*) > 0 AND MIN(working_hours_start_min) IS NOT NULL
        AND MIN(working_hours_end_min) IS NOT NULL
        AND MIN(slot_interval_min) IS NOT NULL
      THEN 1 ELSE 0 END AS ok
      FROM appointment_calendar_settings`,
  },
  {
    key: 'categories',
    label: 'Appointment categories',
    countSql: 'SELECT COUNT(*) AS cnt FROM appointment_categories WHERE active = 1',
    sanitySql: `SELECT CASE
      WHEN COUNT(*) > 0 AND MIN(name) IS NOT NULL AND MIN(color_hex) IS NOT NULL
      THEN 1 ELSE 0 END AS ok
      FROM appointment_categories WHERE active = 1`,
  },
  {
    key: 'statuses',
    label: 'Appointment statuses',
    countSql: 'SELECT COUNT(*) AS cnt FROM appointment_statuses WHERE active = 1',
    sanitySql: `SELECT CASE
      WHEN COUNT(*) > 0 AND MIN(label) IS NOT NULL AND MIN(color_hex) IS NOT NULL
      THEN 1 ELSE 0 END AS ok
      FROM appointment_statuses WHERE active = 1`,
  },
  {
    key: 'services',
    label: 'Appointment services',
    countSql: 'SELECT COUNT(*) AS cnt FROM appointment_services',
    sanitySql: `SELECT CASE
      WHEN COUNT(*) > 0 AND MIN(name) IS NOT NULL
      THEN 1 ELSE 0 END AS ok
      FROM appointment_services`,
  },
  {
    key: 'providers',
    label: 'Appointment providers',
    countSql: `SELECT (SELECT COUNT(*) FROM appointment_providers) + (SELECT COUNT(*) FROM appointment_provider_types) AS cnt`,
    sanitySql: `SELECT CASE
      WHEN (SELECT COUNT(*) FROM appointment_providers) > 0
        AND (SELECT MIN(full_name) FROM appointment_providers) IS NOT NULL
        AND (SELECT COUNT(*) FROM appointment_provider_types) > 0
      THEN 1 ELSE 0 END AS ok`,
  },
];

// ─── Hook ───

const POLL_INTERVAL_MS = 1_500;

export function useInitialSyncMilestones(): InitialSyncMilestones {
  const { db } = usePowerSyncDb();
  const [milestones, setMilestones] = useState<MilestoneStatus[]>(() =>
    MILESTONE_DEFS.map((d) => ({
      key: d.key,
      label: d.label,
      done: false,
      count: 0,
      sanityOk: false,
    })),
  );
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const poll = useCallback(async () => {
    if (!db) return;

    const results: MilestoneStatus[] = [];

    for (const def of MILESTONE_DEFS) {
      try {
        const countRows = await db.getAll<{ cnt: number }>(def.countSql);
        const count = countRows[0]?.cnt ?? 0;

        let sanityOk = true;
        if (def.sanitySql && count > 0) {
          const sanityRows = await db.getAll<{ ok: number }>(def.sanitySql);
          sanityOk = (sanityRows[0]?.ok ?? 0) === 1;
        }

        results.push({
          key: def.key,
          label: def.label,
          done: count > 0 && sanityOk,
          count,
          sanityOk,
        });
      } catch {
        results.push({
          key: def.key,
          label: def.label,
          done: false,
          count: 0,
          sanityOk: false,
        });
      }
    }

    setMilestones(results);
  }, [db]);

  useEffect(() => {
    if (!db) return;

    // Immediate check
    poll();

    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [db, poll]);

  const allComplete = milestones.every((m) => m.done);
  const doneCount = milestones.filter((m) => m.done).length;
  const progress = allComplete
    ? 100
    : Math.max(10, Math.round((doneCount / milestones.length) * 100));

  return { milestones, allComplete, progress };
}
